import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { CourseworkRefusal } from "../CourseworkRefusal";
import { Bento } from "@/components/ui/box";
import type { NativeCourseworkRead } from "@/lib/api/assignments";
import type { Box } from "@/components/ui/box.schema";

type Publish = (id: string, model: Box | null) => void;
const WorkModels = createContext<Publish | null>(null);
const WorkRefusals = createContext<((id: string, refusal: ReactNode | null) => void) | null>(null);
type RowListener = (box: string, row: string, open: boolean) => void;
const WorkRows = createContext<((id: string, listener: RowListener | null) => void) | null>(null);

/**
 * Domain controllers keep their queries and private drafts; one owning page
 * passes their models to the existing primitive. Registration never ranks or
 * renders a second page, and the authored id order never follows effect order.
 */
export function WorkBoxHost({ id, journey, order, children, boxes = [], defaultOpen = [], pageRanking }: {
  id: string; journey: string; order: string[]; children: ReactNode; boxes?: Box[]; defaultOpen?: string[]; pageRanking?: NativeCourseworkRead["ranking"] | null;
}) {
  const [refusals, setRefusals] = useState<ReadonlyMap<string, ReactNode>>(() => new Map());
  const refuse = useCallback((key: string, node: ReactNode | null) => setRefusals(previous => { const next = new Map(previous); if (node === null) next.delete(key); else next.set(key, node); return next; }), []);
  const rowListeners = useRef(new Map<string, RowListener>());
  const registerRows = useCallback((key: string, listener: RowListener | null) => { if (listener) rowListeners.current.set(key, listener); else rowListeners.current.delete(key); }, []);
  const [models, setModels] = useState<ReadonlyMap<string, Box>>(() => new Map());
  const publish = useCallback<Publish>((key, model) => {
    setModels(previous => {
      if (previous.get(key) === model || (model === null && !previous.has(key))) return previous;
      const next = new Map(previous);
      if (model === null) next.delete(key); else next.set(key, model);
      return next;
    });
  }, []);
  const fixed = new Map(boxes.map(box => [box.id, box]));
  const drawn = order.flatMap(key => {
    const box = fixed.get(key) ?? models.get(key);
    return box ? [box] : [];
  });
  const complete = pageRanking?.state === "ready" && drawn.every(box => !box.candidate?.eligible || pageRanking.value.candidates.some(candidate => candidate.id === box.id));
  const ranked = pageRanking === undefined ? drawn : drawn.map(box => box.candidate ? { ...box, candidate: { ...box.candidate, rank: complete && pageRanking?.state === "ready" ? pageRanking.value.candidates.find(candidate => candidate.id === box.id)?.rank ?? null : null } } as Box : box);
  return <WorkRefusals.Provider value={refuse}><WorkModels.Provider value={publish}><WorkRows.Provider value={registerRows}>{children}
    {refusals.size ? [...refusals.values()][0] : <Bento id={id} journey={journey} boxes={ranked} defaultOpen={defaultOpen} onRowOpenChange={(box, row, open) => rowListeners.current.forEach(listener => listener(box, row, open))} />}
  </WorkRows.Provider></WorkModels.Provider></WorkRefusals.Provider>;
}

/** Reuse native controllers in an addressed visit without mounting another Bento. */
export function WorkModelSink({ publish, children }: { publish: Publish; children: ReactNode }) {
  return <WorkModels.Provider value={publish}>{children}</WorkModels.Provider>;
}

/** Outside a host the same model is a standalone work; inside it no page nests. */
export function WorkBox({ box, defaultOpen = [] }: { box: Box; defaultOpen?: string[] }) {
  const publish = useContext(WorkModels);
  useLayoutEffect(() => { publish?.(box.id, box); }, [publish, box]);
  useLayoutEffect(() => () => { publish?.(box.id, null); }, [publish, box.id]);
  return publish ? null : <Bento id={`work-${box.id}`} journey="hand-in" boxes={[box]} defaultOpen={defaultOpen} />;
}

/** A multi-box controller still uses one page outside a shared host. */
export function WorkBoxes({ boxes, journey, id, zone, defaultOpen = [], onRowOpenChange }: { boxes: Box[]; journey: string; id?: string; zone?: string; defaultOpen?: string[]; onRowOpenChange?: RowListener }) {
  const publish = useContext(WorkModels);
  const registerRows = useContext(WorkRows);
  const key = boxes[0]?.id ?? journey;
  useLayoutEffect(() => { registerRows?.(key, onRowOpenChange ?? null); return () => registerRows?.(key, null); }, [registerRows, key, onRowOpenChange]);
  return publish ? <>{boxes.map(box => <WorkBox key={box.id} box={box} />)}</> : <Bento id={id ?? `${journey}-${boxes[0]?.id ?? "empty"}`} journey={journey} zone={zone} boxes={boxes} defaultOpen={defaultOpen} onRowOpenChange={onRowOpenChange} />;
}

/** An unavailable exact address refuses its owning page, including other reads on that page. */
export function WorkPageRefusal({ id, children }: { id: string; children: ReactNode }) {
  const refuse = useContext(WorkRefusals);
  const content = useMemo(() => <CourseworkRefusal id={id}>{children}</CourseworkRefusal>, [id, children]);
  useLayoutEffect(() => { refuse?.(id, content); return () => refuse?.(id, null); }, [refuse, id, content]);
  return refuse ? null : content;
}
